import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  getSite, listSignInsBetween,
  type Site, type SignIn,
} from '../lib/db';
import {
  weekOf, previousWeek, nextWeek,
  buildTimesheet, fmtHours,
  summaryCsv, detailedCsv, generateTimesheetPdf,
  type WeekRange,
} from '../lib/timesheet';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function Timesheet() {
  const { siteId } = useParams<{ siteId: string }>();
  const { org } = useUserOrg();

  const [site, setSite] = useState<Site | null>(null);
  const [week, setWeek] = useState<WeekRange>(() => weekOf(new Date()));
  const [signIns, setSignIns] = useState<SignIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const [s, ins] = await Promise.all([
          getSite(siteId),
          listSignInsBetween(siteId, week.startIso, week.endIso),
        ]);
        if (cancelled) return;
        setSite(s); setSignIns(ins);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, week]);

  const grid = useMemo(() => buildTimesheet(week, signIns), [week, signIns]);

  function fileNameBase(): string {
    const safeName = (site?.name ?? 'site').replace(/[^a-zA-Z0-9]+/g, '-');
    const dateStr = week.startDate.toISOString().slice(0, 10);
    return `timesheet-${safeName}-week-of-${dateStr}`;
  }

  function downloadCsv(content: string, suffix: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileNameBase()}-${suffix}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function downloadPdf(): void {
    if (!site || !org) return;
    const blob = generateTimesheetPdf({
      orgName: org.org_name,
      siteName: site.name,
      week,
      grid,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileNameBase()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function sharePdf(): Promise<void> {
    if (!site || !org) return;
    setShareBusy(true);
    try {
      const blob = generateTimesheetPdf({
        orgName: org.org_name,
        siteName: site.name,
        week,
        grid,
      });
      const file = new File([blob], `${fileNameBase()}.pdf`, { type: 'application/pdf' });
      const navAny = navigator as unknown as {
        canShare?: (data: { files?: File[] }) => boolean;
        share?: (data: { title?: string; text?: string; files?: File[] }) => Promise<void>;
      };
      if (navAny.canShare && navAny.canShare({ files: [file] }) && navAny.share) {
        await navAny.share({
          title: `Sign-in log — ${site.name}`,
          text: buildShareText(),
          files: [file],
        });
        return;
      }
      // Fallback: just download
      downloadPdf();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (!/cancel/i.test(msg)) setError(msg);
    } finally {
      setShareBusy(false);
    }
  }

  async function shareSummary(): Promise<void> {
    setShareBusy(true);
    try {
      const csv = summaryCsv(grid, week);
      const file = new File([csv], `${fileNameBase()}-summary.csv`, { type: 'text/csv' });
      const text = buildShareText();
      // Web Share API w/ files (iOS 15+ Safari, Android Chrome)
      const navAny = navigator as unknown as {
        canShare?: (data: { files?: File[] }) => boolean;
        share?: (data: { title?: string; text?: string; files?: File[] }) => Promise<void>;
      };
      if (navAny.canShare && navAny.canShare({ files: [file] }) && navAny.share) {
        await navAny.share({
          title: `Site sign-in log — ${site?.name ?? ''}`,
          text,
          files: [file],
        });
        return;
      }
      // Fallback — download CSV + open mailto with body so user can attach
      downloadCsv(csv, 'summary');
      window.location.href = `mailto:?subject=${encodeURIComponent(`Sign-in log — ${site?.name ?? ''} — week of ${week.label}`)}&body=${encodeURIComponent(text)}`;
    } catch (e) {
      // User cancelled share is normal — only show real errors
      const msg = e instanceof Error ? e.message : String(e);
      if (!/cancel/i.test(msg)) setError(msg);
    } finally {
      setShareBusy(false);
    }
  }

  function buildShareText(): string {
    const lines: string[] = [];
    lines.push(`Site sign-in log — ${site?.name ?? ''}`);
    lines.push(`Week of ${week.label}`);
    lines.push('');
    if (grid.rows.length === 0) {
      lines.push('No sign-ins this week.');
    } else {
      for (const p of grid.rows) {
        const days = grid.dayKeys.map((dk, i) => {
          const h = p.hoursByDay[dk] ?? 0;
          return h > 0 ? `${DAY_LABELS[i]} ${fmtHours(h)}` : '';
        }).filter(Boolean).join(' · ');
        lines.push(`${p.fullName}${p.company ? ' (' + p.company + ')' : ''} — ${fmtHours(p.totalHours)} hrs total`);
        if (days) lines.push(`  ${days}`);
        if (p.hasOpen) lines.push(`  ⚠ open sign-in — check before paying`);
      }
      lines.push('');
      lines.push(`Total hours this week: ${fmtHours(grid.totalHours)}`);
    }
    lines.push('');
    lines.push('Generated by ' + (org?.org_name ?? 'site sign-in app'));
    return lines.join('\n');
  }

  function setThisWeek()      { setWeek(weekOf(new Date())); }
  function setLastWeek()      { setWeek(previousWeek(weekOf(new Date()))); }
  function goPrevious()       { setWeek(previousWeek(week)); }
  function goNext()            { setWeek(nextWeek(week)); }

  if (loading && !site) return <AppShell title="Timesheet" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  if (error)             return <AppShell title="Timesheet" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error}</div></AppShell>;
  if (!site)             return <AppShell title="Timesheet" back="/"><div className="banner banner-error">Site not found.</div></AppShell>;

  const hasOpenAny = grid.rows.some((p) => p.hasOpen);

  return (
    <AppShell title="Sign-in log / hours" back={`/sites/${siteId}`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>{site.name}</p>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Hours worked, by person, by day. Use it for wages or send it to whoever does your payroll.
        </p>
      </div>

      <div className="ts-week-bar">
        <button type="button" className="btn-tiny" onClick={goPrevious} aria-label="Previous week">‹</button>
        <span className="ts-week-label">{week.label}</span>
        <button type="button" className="btn-tiny" onClick={goNext} aria-label="Next week">›</button>
      </div>

      <div className="ts-quick-row">
        <button type="button" className="btn-tiny" onClick={setThisWeek}>This week</button>
        <button type="button" className="btn-tiny" onClick={setLastWeek}>Last week</button>
      </div>

      {hasOpenAny && (
        <div className="banner banner-warn" style={{ marginTop: 12 }}>
          ⚠ Some sign-ins this week have no sign-out. Hours for those rows will be missing — check before sending for wages.
        </div>
      )}

      {grid.rows.length === 0 ? (
        <div className="card" style={{ marginTop: 12, textAlign: 'center' }}>
          <p className="muted" style={{ margin: 0 }}>No sign-ins this week.</p>
        </div>
      ) : (
        <>
          <div className="ts-table-wrap">
            <table className="ts-table">
              <thead>
                <tr>
                  <th>Person</th>
                  {DAY_LABELS.map((d, i) => (
                    <th key={d} title={grid.dayDates[i].toLocaleDateString('en-GB')}>{d}</th>
                  ))}
                  <th className="ts-total-col">Total</th>
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((p) => (
                  <tr key={p.name}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.fullName}</div>
                      {p.company && <div className="muted" style={{ fontSize: 11 }}>{p.company}</div>}
                      {p.hasOpen && <div style={{ fontSize: 11, color: 'var(--danger)' }}>open sign-in</div>}
                    </td>
                    {grid.dayKeys.map((dk) => (
                      <td key={dk}>{fmtHours(p.hoursByDay[dk] ?? 0) || '—'}</td>
                    ))}
                    <td className="ts-total-col" style={{ fontWeight: 700 }}>{fmtHours(p.totalHours) || '—'}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td style={{ fontWeight: 700 }}>Daily total</td>
                  {grid.dayKeys.map((dk) => (
                    <td key={dk}>{fmtHours(grid.totalsByDay[dk] ?? 0) || '—'}</td>
                  ))}
                  <td className="ts-total-col" style={{ fontWeight: 700 }}>{fmtHours(grid.totalHours) || '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="btn-stack" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-primary" onClick={sharePdf} disabled={shareBusy}>
              {shareBusy ? 'Opening share…' : '📨 Email / share as PDF'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={downloadPdf}>
              Download PDF
            </button>
            <button type="button" className="btn btn-ghost" onClick={shareSummary} disabled={shareBusy}>
              📨 Email / share summary CSV
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => downloadCsv(summaryCsv(grid, week), 'summary')}>
              Download summary CSV
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => downloadCsv(detailedCsv(signIns), 'detailed')}>
              Download detailed CSV (every sign-in row)
            </button>
          </div>

          <p className="muted" style={{ marginTop: 16, fontSize: 12 }}>
            Hours are calculated from each sign-in/sign-out pair. Open sign-ins (no sign-out) show as 0 hours and a warning.
            The summary CSV is structured one row per person, one column per day — drop it into any spreadsheet or share with your accountant.
          </p>
        </>
      )}
    </AppShell>
  );
}
